/**
 * Doc video upload — presign → PUT to R2 → confirm.
 * Uses the same public bucket as doc images; counts toward the shared
 * 100GB attachment storage pool.
 */

import { pletApiRequest } from '../utils/api/client';
import {
  DOC_VIDEO_ALLOWED_TYPES,
  DOC_VIDEO_MAX_BYTES,
  validateDocVideoFile,
} from '../components/DocEditor/utils/videoFiles';

/**
 * Upload a doc video file to R2.
 * @param {string} docId
 * @param {File} file
 * @param {(n: number) => void} [onProgress]
 * @returns {Promise<{ videoId: string, publicUrl: string, r2Key: string, fileName: string, mimeType: string, size: number }>}
 */
export async function uploadDocVideo(docId, file, onProgress = () => {}) {
  if (!docId) throw new Error('Doc must be saved before uploading videos');

  const check = validateDocVideoFile(file);
  if (!check.ok) throw new Error(check.error);

  onProgress(5);

  const { uploadUrl, videoId, r2Key } = await pletApiRequest('/doc-videos/upload-url', {
    method: 'POST',
    body: JSON.stringify({
      docId,
      fileName: file.name,
      mimeType: file.type,
      size: file.size,
    }),
  });

  onProgress(12);

  // XHR so we can report real byte progress during the R2 PUT
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener('progress', (e) => {
      if (!e.lengthComputable) return;
      onProgress(12 + (e.loaded / e.total) * 73);
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error('Failed to upload video to storage'));
    });
    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'));
    });
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });

  onProgress(90);

  const result = await pletApiRequest('/doc-videos/confirm', {
    method: 'POST',
    body: JSON.stringify({ videoId }),
  });

  onProgress(100);

  return {
    videoId: result.videoId,
    publicUrl: result.publicUrl,
    r2Key: result.r2Key || r2Key,
    fileName: result.fileName || file.name,
    mimeType: result.mimeType || file.type,
    size: result.size ?? file.size,
  };
}

export const DOC_VIDEO_UPLOAD_LIMITS = {
  maxBytes: DOC_VIDEO_MAX_BYTES,
  allowedTypes: DOC_VIDEO_ALLOWED_TYPES,
};
